import React, { useEffect, useState, useRef, Children, createElement, Component } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { ShieldCheck, Heart, Brain, Check, Clock, FileText, Headphones, Lock, ArrowRight, Star } from 'lucide-react';
// --- Meta Pixel Helper ---
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}
const initMetaPixel = () => {
  try {
    if (typeof window === 'undefined' || window.fbq) return;
    const f = window as any;
    const n: any = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const t = document.createElement('script');
    t.async = true;
    t.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const target = document.head || document.body;
    if (target) {
      target.appendChild(t);
    }
    window.fbq('init', '2758147827885870');
    window.fbq('track', 'PageView');
  } catch (e) {
    // Meta Pixel init failed silently — non-blocking
  }
};
const trackAddToCart = () => {
  try {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: 'Corta el Ciclo de la Ansiedad Financiera',
        content_type: 'product',
        value: 6.36,
        currency: 'USD'
      });
    }
  } catch (e) {
    // tracking failed silently
  }
};
// --- Animation Components ---
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 30
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 30
  }} transition={{
    duration: 0.8,
    delay,
    ease: 'easeOut'
  }} className={className} data-id="element-1">
      {children}
    </motion.div>;
};
const StaggerContainer = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  return <motion.div ref={ref} initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={{
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  }} className={className} data-id="element-2">
      {children}
    </motion.div>;
};
const StaggerItem = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <motion.div variants={{
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  }} className={className} data-id="element-3">
      {children}
    </motion.div>;
};
const PulseButton = ({
  children,
  className = '',
  onClick
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) => {
  return <motion.button whileHover={{
    scale: 1.05
  }} whileTap={{
    scale: 0.95
  }} animate={{
    boxShadow: ['0 0 0 0 rgba(181, 84, 31, 0.4)', '0 0 0 10px rgba(181, 84, 31, 0)']
  }} transition={{
    boxShadow: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  }} className={`relative overflow-hidden ${className}`} onClick={onClick} data-id="element-4">
      {children}
    </motion.button>;
};
// --- Legal Modal Component ---
const LegalModal = ({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-5">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-6" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} className="relative bg-[var(--color-bg)] rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-7">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-[var(--color-alt-bg)]" data-id="element-8">
          <h3 className="text-xl font-bold text-[var(--color-text)]" data-id="element-9">
            {title}
          </h3>
          <button onClick={onClose} className="text-[var(--color-muted)] hover:text-[var(--color-text)] transition-colors text-2xl leading-none font-light" aria-label="Cerrar" data-id="element-10">
            ×
          </button>
        </div>
        {/* Modal Body */}
        <div className="px-8 py-6 overflow-y-auto max-h-[60vh] text-[var(--color-text)] font-serif text-base leading-relaxed space-y-4" data-id="element-11">
          {children}
        </div>
      </motion.div>
    </div>;
};
// --- Main Page Component ---
export function SalesPage() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  useEffect(() => {
    initMetaPixel();
  }, []);
  const handleBuyClick = () => {
    trackAddToCart();
    window.open('https://estadovittal.com/cart/47862543614187:1', '_blank');
  };
  const scrollToOffer = () => {
    const element = document.getElementById('offer');
    if (element) element.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen w-full overflow-x-hidden" data-id="element-12">
      {/* 1. Top Bar */}
      <div className="bg-[var(--color-accent)] text-white py-2 px-4 text-center font-ui text-sm font-medium tracking-wide" data-id="element-13">
        <p data-id="element-14">⏰ Precio especial de lanzamiento — Solo por tiempo limitado</p>
      </div>

      {/* 2. Hero Section */}
      <section className="relative py-12 md:py-20 px-4 md:px-8 max-w-7xl mx-auto" data-id="element-15">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-16">
          {/* Left Column: Copy */}
          <div className="order-2 md:order-1 space-y-8" data-id="element-17">
            <FadeIn data-id="element-18">
              <span className="inline-block py-1 px-3 rounded-full bg-[var(--color-alt-bg)] text-[var(--color-accent)] font-ui text-xs font-bold tracking-wider uppercase mb-4" data-id="element-19">
                Para la mujer que lo da todo y ya no puede más
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-[var(--color-text)] mb-6" data-id="element-20">
                Respira primero, <br data-id="element-21" />
                <span className="italic text-[var(--color-accent)]" data-id="element-22">
                  paga después.
                </span>
              </h1>
              <h2 className="text-xl md:text-2xl text-[var(--color-muted)] font-medium mb-6" data-id="element-23">
                El protocolo de 10 minutos para mujeres que se despiertan a las
                2am pensando en deudas.
              </h2>
              <p className="text-lg text-[var(--color-text)] leading-relaxed mb-8" data-id="element-24">
                No es falta de voluntad. No eres mala con el dinero. Tu cuerpo
                está en modo de emergencia — y hay una salida.
              </p>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-25">
                <PulseButton onClick={scrollToOffer} className="bg-[var(--color-accent)] text-white font-ui font-semibold text-lg py-4 px-8 rounded-full shadow-lg hover:bg-[#9a4619] transition-colors w-full sm:w-auto text-center" data-id="element-26">
                  Quiero mi paz mental{' '}
                  <ArrowRight className="inline ml-2 h-5 w-5" data-id="element-27" />
                </PulseButton>
              </div>

              <div className="flex flex-wrap gap-4 text-sm text-[var(--color-muted)] font-ui mt-4" data-id="element-28">
                <span className="flex items-center" data-id="element-29">
                  <Check className="h-4 w-4 mr-1 text-[var(--color-secondary)]" data-id="element-30" />{' '}
                  Acceso inmediato
                </span>
                <span className="flex items-center" data-id="element-31">
                  <Check className="h-4 w-4 mr-1 text-[var(--color-secondary)]" data-id="element-32" />{' '}
                  Garantía de 7 días
                </span>
                <span className="flex items-center" data-id="element-33">
                  <Check className="h-4 w-4 mr-1 text-[var(--color-secondary)]" data-id="element-34" />{' '}
                  En español
                </span>
              </div>
            </FadeIn>
          </div>

          {/* Right Column: Image */}
          <div className="order-1 md:order-2" data-id="element-35">
            <FadeIn delay={0.2} data-id="element-36">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl rotate-1 hover:rotate-0 transition-transform duration-500" data-id="element-37">
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-text)]/30 to-transparent mix-blend-multiply" data-id="element-38"></div>
                <img src="https://cdn.magicpatterns.com/uploads/sUWfuChPjvdAmD1PMnsR4V/heylloyd-image-1771819615116.png" alt="Mujer latina en paz tomando café en su cocina por la noche" className="w-full h-auto object-cover" data-id="element-39" />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 3. Lead / Opening Story */}
      <section className="py-16 md:py-24 bg-[var(--color-alt-bg)] px-4" data-id="element-40">
        <div className="max-w-3xl mx-auto" data-id="element-41">
          <FadeIn data-id="element-42">
            <blockquote className="text-2xl md:text-3xl font-serif italic text-[var(--color-text)] text-center leading-relaxed mb-12 border-l-4 border-[var(--color-secondary)] pl-6 md:border-none md:pl-0" data-id="element-43">
              "Son las 2 de la mañana. Tus hijos duermen, pero tú llevas dos
              horas mirando el techo, haciendo cuentas mentales que nunca
              cuadran."
            </blockquote>
          </FadeIn>

          <div className="space-y-6 text-lg md:text-xl leading-relaxed text-[var(--color-text)] font-serif" data-id="element-44">
            <FadeIn delay={0.2} data-id="element-45">
              <p data-id="element-46">
                Ese nudo en el estómago ya es parte de tu cuerpo. Ese miedo a
                que suene el teléfono o a que llegue otro sobre amarillo te ha
                robado la alegría de vivir.
              </p>
            </FadeIn>
            <FadeIn delay={0.3} data-id="element-47">
              <p data-id="element-48">
                Has intentado hacer hojas de cálculo. Has descargado apps. Has
                visto videos de YouTube. Pero cuando intentas sentarte a
                'organizarte', el pecho se te aprieta y terminas cerrando la
                computadora.
              </p>
            </FadeIn>
            <FadeIn delay={0.4} data-id="element-49">
              <p className="font-bold text-[var(--color-accent)]" data-id="element-50">
                Aquí está la verdad que nadie te dice: No es que seas 'mala con
                el dinero'. Es que tu cuerpo está en modo de emergencia.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 4. The Problem (Identity Mirror) */}
      <section className="py-16 md:py-24 px-4 max-w-5xl mx-auto" data-id="element-51">
        <div className="text-center mb-16" data-id="element-52">
          <FadeIn data-id="element-53">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-[var(--color-text)]" data-id="element-54">
              ¿Por qué "saber matemáticas" no es suficiente?
            </h2>
            <div className="w-24 h-1 bg-[var(--color-secondary)] mx-auto rounded-full" data-id="element-55"></div>
          </FadeIn>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-56">
          <FadeIn data-id="element-57">
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-[var(--color-alt-bg)]" data-id="element-58">
              <h3 className="text-2xl font-bold mb-4 text-[var(--color-text)] flex items-center" data-id="element-59">
                <Brain className="h-6 w-6 mr-3 text-[var(--color-accent)]" data-id="element-60" />
                El Secuestro de la Amígdala
              </h3>
              <p className="text-lg mb-4" data-id="element-61">
                Cuando ves una deuda, tu cerebro primitivo no ve "números". Ve
                un león a punto de atacarte.
              </p>
              <p className="text-lg" data-id="element-62">
                Tu cuerpo se inunda de cortisol. Tu corteza prefrontal (la parte
                que planea y razona) se apaga. Literalmente,{' '}
                <strong data-id="element-63">no puedes pensar con claridad</strong> cuando estás en
                pánico.
              </p>
            </div>
          </FadeIn>

          <StaggerContainer className="space-y-6" data-id="element-64">
            <StaggerItem className="flex items-start" data-id="element-65">
              <div className="bg-[var(--color-alt-bg)] p-3 rounded-full mr-4 flex-shrink-0" data-id="element-66">
                <Heart className="h-6 w-6 text-[var(--color-accent)]" data-id="element-67" />
              </div>
              <div data-id="element-68">
                <h4 className="font-bold text-lg mb-1 font-ui" data-id="element-69">
                  La vergüenza tóxica
                </h4>
                <p className="text-[var(--color-muted)]" data-id="element-70">
                  Sientes que has fallado a tu familia, lo que te lleva a
                  esconderte y evitar el problema.
                </p>
              </div>
            </StaggerItem>
            <StaggerItem className="flex items-start" data-id="element-71">
              <div className="bg-[var(--color-alt-bg)] p-3 rounded-full mr-4 flex-shrink-0" data-id="element-72">
                <Lock className="h-6 w-6 text-[var(--color-accent)]" data-id="element-73" />
              </div>
              <div data-id="element-74">
                <h4 className="font-bold text-lg mb-1 font-ui" data-id="element-75">
                  Parálisis por análisis
                </h4>
                <p className="text-[var(--color-muted)]" data-id="element-76">
                  Sabes que tienes que hacer algo, pero el miedo te congela y no
                  haces nada.
                </p>
              </div>
            </StaggerItem>
            <StaggerItem className="flex items-start" data-id="element-77">
              <div className="bg-[var(--color-alt-bg)] p-3 rounded-full mr-4 flex-shrink-0" data-id="element-78">
                <Clock className="h-6 w-6 text-[var(--color-accent)]" data-id="element-79" />
              </div>
              <div data-id="element-80">
                <h4 className="font-bold text-lg mb-1 font-ui" data-id="element-81">
                  El ciclo del cansancio
                </h4>
                <p className="text-[var(--color-muted)]" data-id="element-82">
                  Trabajas duro, pero la ansiedad drena tu energía vital,
                  dejándote sin fuerza para negociar.
                </p>
              </div>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </section>

      {/* 5. The Solution (Product Reveal) */}
      <section className="py-20 bg-[var(--color-text)] text-[var(--color-bg)] px-4" data-id="element-83">
        <div className="max-w-4xl mx-auto text-center" data-id="element-84">
          <FadeIn data-id="element-85">
            <span className="font-ui tracking-[0.2em] text-[var(--color-secondary)] uppercase text-sm font-bold mb-4 block" data-id="element-86">
              Presentando
            </span>
            <h2 className="text-4xl md:text-6xl font-bold mb-8 text-white" data-id="element-87">
              Corta el Ciclo de la Ansiedad Financiera
            </h2>
            <p className="text-xl md:text-2xl font-serif italic text-[var(--color-alt-bg)] mb-12" data-id="element-88">
              Protocolo Somático para calmar tu cuerpo y liberar tu mente
            </p>
            <p className="text-lg md:text-xl leading-relaxed opacity-90 mb-12 max-w-2xl mx-auto" data-id="element-89">
              Un método diseñado para bajar el cortisol antes de tocar un solo
              dólar. Porque una mujer en calma es una mujer que puede negociar,
              planear y vencer.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 6. What's Included */}
      <section className="py-20 px-4 max-w-6xl mx-auto" data-id="element-90">
        <div className="text-center mb-16" data-id="element-91">
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text)] mb-4" data-id="element-92">
            ¿Qué incluye el protocolo?
          </h2>
          <p className="text-[var(--color-muted)] text-lg" data-id="element-93">
            Todo lo que necesitas para recuperar el control hoy mismo
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-94">
          <FadeIn delay={0.1} data-id="element-95">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-[var(--color-alt-bg)] hover:shadow-md transition-shadow h-full" data-id="element-96">
              <div className="w-12 h-12 bg-[var(--color-alt-bg)] rounded-full flex items-center justify-center mb-6" data-id="element-97">
                <ShieldCheck className="h-6 w-6 text-[var(--color-accent)]" data-id="element-98" />
              </div>
              <h3 className="text-xl font-bold mb-3 font-ui" data-id="element-99">
                El Protocolo 4-7-8 para llamadas
              </h3>
              <p className="text-[var(--color-muted)]" data-id="element-100">
                Una técnica de respiración específica para usar ANTES de
                contestar el teléfono o abrir una carta de cobranza. Calma tu
                sistema nervioso en 90 segundos.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-101">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-[var(--color-alt-bg)] hover:shadow-md transition-shadow h-full" data-id="element-102">
              <div className="w-12 h-12 bg-[var(--color-alt-bg)] rounded-full flex items-center justify-center mb-6" data-id="element-103">
                <FileText className="h-6 w-6 text-[var(--color-accent)]" data-id="element-104" />
              </div>
              <h3 className="text-xl font-bold mb-3 font-ui" data-id="element-105">
                Scripts de Negociación "Cero Miedo"
              </h3>
              <p className="text-[var(--color-muted)]" data-id="element-106">
                Guiones exactos, palabra por palabra, de qué decir para pedir
                prórrogas o negociar pagos sin que te tiemble la voz. Solo
                tienes que leer.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-107">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-[var(--color-alt-bg)] hover:shadow-md transition-shadow h-full" data-id="element-108">
              <div className="w-12 h-12 bg-[var(--color-alt-bg)] rounded-full flex items-center justify-center mb-6" data-id="element-109">
                <Star className="h-6 w-6 text-[var(--color-accent)]" data-id="element-110" />
              </div>
              <h3 className="text-xl font-bold mb-3 font-ui" data-id="element-111">
                El Ritual de 15 Minutos
              </h3>
              <p className="text-[var(--color-muted)]" data-id="element-112">
                Una rutina paso a paso para abrir facturas sin llorar.
                Transforma el momento más temido del mes en un acto de valentía
                y autocuidado.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-113">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-[var(--color-alt-bg)] hover:shadow-md transition-shadow h-full" data-id="element-114">
              <div className="w-12 h-12 bg-[var(--color-alt-bg)] rounded-full flex items-center justify-center mb-6" data-id="element-115">
                <Heart className="h-6 w-6 text-[var(--color-accent)]" data-id="element-116" />
              </div>
              <h3 className="text-xl font-bold mb-3 font-ui" data-id="element-117">
                Inventario "Sin Juicio"
              </h3>
              <p className="text-[var(--color-muted)]" data-id="element-118">
                Una herramienta para separar tu valor humano de tu puntaje de
                crédito. Porque tu deuda no define quién eres como mujer o
                madre.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 7. Offer & Bonuses */}
      <section id="offer" className="py-20 bg-[var(--color-alt-bg)] px-4" data-id="element-119">
        <div className="max-w-4xl mx-auto" data-id="element-120">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-[var(--color-secondary)]/20" data-id="element-121">
            <div className="bg-[var(--color-text)] text-white py-4 text-center" data-id="element-122">
              <span className="font-ui font-bold tracking-widest uppercase text-sm" data-id="element-123">
                Oferta Especial
              </span>
            </div>

            <div className="p-8 md:p-12 text-center" data-id="element-124">
              <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text)] mb-2" data-id="element-125">
                Corta el Ciclo de la Ansiedad Financiera
              </h2>
              <p className="text-[var(--color-muted)] mb-8" data-id="element-126">
                Edición Digital Completa
              </p>

              <div className="flex justify-center items-baseline gap-3 mb-8" data-id="element-127">
                <span className="text-3xl md:text-4xl font-bold text-[var(--color-muted)] line-through opacity-60" data-id="element-128">
                  $27
                </span>
                <span className="text-5xl md:text-6xl font-bold text-[var(--color-accent)]" data-id="element-129">
                  $6.36
                </span>
                <span className="text-xl text-[var(--color-muted)] ml-1" data-id="element-130">
                  USD
                </span>
              </div>

              <div className="inline-block bg-[var(--color-secondary)]/20 text-[var(--color-accent)] font-ui font-bold text-sm px-4 py-1.5 rounded-full mb-8 tracking-wide uppercase" data-id="element-131">
                🔥 77% de descuento — Solo por tiempo limitado
              </div>

              <PulseButton onClick={handleBuyClick} className="w-full md:w-auto bg-[var(--color-accent)] text-white font-ui font-bold text-xl py-5 px-10 rounded-full shadow-lg hover:bg-[#9a4619] transition-colors mb-6" data-id="element-132">
                Descargar Ahora — $6.36 USD
              </PulseButton>

              <p className="text-sm text-[var(--color-muted)]" data-id="element-133">
                Pago seguro y encriptado. Entrega inmediata a tu email.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Guarantee */}
      <section className="py-16 px-4 max-w-3xl mx-auto text-center" data-id="element-134">
        <FadeIn data-id="element-135">
          <div className="border-2 border-dashed border-[var(--color-secondary)] rounded-2xl p-8 md:p-12 bg-white/50" data-id="element-136">
            <ShieldCheck className="h-12 w-12 text-[var(--color-secondary)] mx-auto mb-6" data-id="element-137" />
            <h3 className="text-2xl font-bold text-[var(--color-text)] mb-4" data-id="element-138">
              Garantía de Paz de 7 Días
            </h3>
            <p className="text-lg text-[var(--color-muted)] leading-relaxed" data-id="element-139">
              Prueba el protocolo sin riesgo. Si en 7 días no sientes que puedes
              respirar mejor al pensar en tus finanzas, escríbenos y te
              devolvemos el 100% de tu dinero. Sin preguntas.
            </p>
          </div>
        </FadeIn>
      </section>

      {/* Footer */}
      <footer className="bg-[var(--color-text)] text-[var(--color-alt-bg)] py-12 px-4 text-center" data-id="element-140">
        <p className="font-serif italic text-xl mb-6" data-id="element-141">
          "Tu paz mental vale más que tus saldos."
        </p>
        <div className="text-sm opacity-60 font-ui space-y-2 mb-6" data-id="element-142">
          <p data-id="element-143">
            © 2025 Corta el Ciclo de la Ansiedad Financiera. Todos los derechos
            reservados.
          </p>
          <p data-id="element-144">
            Este producto no sustituye asesoría legal o financiera profesional.
          </p>
        </div>
        <div className="flex justify-center gap-6 text-sm font-ui" data-id="element-145">
          <button onClick={() => setActiveModal('privacy')} className="text-[var(--color-secondary)] hover:text-white underline underline-offset-2 transition-colors" data-id="element-146">
            Política de Privacidad
          </button>
          <span className="text-[var(--color-alt-bg)]/30" data-id="element-147">|</span>
          <button onClick={() => setActiveModal('terms')} className="text-[var(--color-secondary)] hover:text-white underline underline-offset-2 transition-colors" data-id="element-148">
            Términos y Condiciones
          </button>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <LegalModal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-149">
        <p className="text-[var(--color-muted)] text-sm" data-id="element-150">
          Última actualización: 2025
        </p>
        <p data-id="element-151">
          En <strong data-id="element-152">Corta el Ciclo de la Ansiedad Financiera</strong>, nos
          comprometemos a proteger tu privacidad. Esta política describe cómo
          recopilamos, usamos y protegemos tu información personal.
        </p>
        <h4 className="font-bold text-[var(--color-text)] mt-4" data-id="element-153">
          1. Información que recopilamos
        </h4>
        <p data-id="element-154">
          Recopilamos únicamente la información necesaria para procesar tu
          compra y entregarte el producto digital: nombre y dirección de correo
          electrónico. No recopilamos información de pago directamente — esto es
          gestionado de forma segura por nuestro procesador de pagos.
        </p>
        <h4 className="font-bold text-[var(--color-text)] mt-4" data-id="element-155">
          2. Uso de la información
        </h4>
        <p data-id="element-156">
          Tu información se utiliza exclusivamente para: (a) entregar el
          producto digital adquirido, (b) enviarte comunicaciones relacionadas
          con tu compra, y (c) ocasionalmente informarte sobre productos o
          recursos relacionados, de los cuales puedes darte de baja en cualquier
          momento.
        </p>
        <h4 className="font-bold text-[var(--color-text)] mt-4" data-id="element-157">
          3. Protección de datos
        </h4>
        <p data-id="element-158">
          No vendemos, alquilamos ni compartimos tu información personal con
          terceros, excepto cuando sea necesario para procesar tu pago o cumplir
          con obligaciones legales.
        </p>
        <h4 className="font-bold text-[var(--color-text)] mt-4" data-id="element-159">4. Cookies</h4>
        <p data-id="element-160">
          Este sitio puede utilizar cookies básicas para mejorar tu experiencia
          de navegación. No utilizamos cookies de seguimiento de terceros para
          publicidad sin tu consentimiento.
        </p>
        <h4 className="font-bold text-[var(--color-text)] mt-4" data-id="element-161">
          5. Tus derechos
        </h4>
        <p data-id="element-162">
          Tienes derecho a solicitar acceso, corrección o eliminación de tus
          datos personales en cualquier momento.
        </p>
      </LegalModal>

      {/* Terms & Conditions Modal */}
      <LegalModal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-163">
        <p className="text-[var(--color-muted)] text-sm" data-id="element-164">
          Última actualización: 2025
        </p>
        <p data-id="element-165">
          Al adquirir{' '}
          <strong data-id="element-166">
            Corta el Ciclo de la Ansiedad Financiera: Protocolo Somático
          </strong>
          , aceptas los siguientes términos y condiciones.
        </p>
        <h4 className="font-bold text-[var(--color-text)] mt-4" data-id="element-167">
          1. Naturaleza del producto
        </h4>
        <p data-id="element-168">
          Este es un producto digital (PDF) de carácter educativo e informativo.
          No constituye asesoría financiera, legal, médica ni psicológica
          profesional. Los resultados pueden variar según cada persona.
        </p>
        <h4 className="font-bold text-[var(--color-text)] mt-4" data-id="element-169">2. Entrega</h4>
        <p data-id="element-170">
          Recibirás acceso inmediato al producto digital tras confirmar tu pago.
          La entrega se realiza a la dirección de correo electrónico
          proporcionada durante la compra.
        </p>
        <h4 className="font-bold text-[var(--color-text)] mt-4" data-id="element-171">
          3. Garantía de satisfacción
        </h4>
        <p data-id="element-172">
          Ofrecemos una garantía de devolución de 7 días. Si no estás satisfecha
          con el producto, puedes solicitar un reembolso completo dentro de los
          7 días posteriores a tu compra.
        </p>
        <h4 className="font-bold text-[var(--color-text)] mt-4" data-id="element-173">
          4. Propiedad intelectual
        </h4>
        <p data-id="element-174">
          Todo el contenido de este producto es propiedad exclusiva de El Escudo
          Financiero. Queda prohibida su reproducción, distribución o reventa
          total o parcial sin autorización escrita.
        </p>
        <h4 className="font-bold text-[var(--color-text)] mt-4" data-id="element-175">
          5. Limitación de responsabilidad
        </h4>
        <p data-id="element-176">
          El Escudo Financiero no se hace responsable de decisiones financieras
          tomadas basándose en el contenido de este producto. Siempre
          recomendamos consultar con un profesional certificado para situaciones
          financieras específicas.
        </p>
      </LegalModal>
    </div>;
}